/**
 * Header JS
 * Handles sticky positioning offsets if announcement bar is present
 */

class SiteHeader extends HTMLElement {
  constructor() {
    super();
    this.header = this;
    this.announcementBar = document.querySelector('.announcement-bar');
    
    this.updateOffset();
    
    // Listen for resize to adjust offset
    window.addEventListener('resize', this.updateOffset.bind(this));
    
    // Set up a mutation observer in case the announcement bar visibility changes
    if (this.announcementBar) {
      this.observer = new MutationObserver(this.updateOffset.bind(this));
      this.observer.observe(this.announcementBar, { attributes: true, attributeFilter: ['style', 'data-visible'] });
    }
  }

  updateOffset() {
    if (!this.announcementBar) {
      document.documentElement.style.setProperty('--announcement-bar-height', '0px');
      return;
    }

    const isVisible = this.announcementBar.getAttribute('data-visible') === 'true' || 
                      window.getComputedStyle(this.announcementBar).display !== 'none';
                      
    if (isVisible) {
      const height = this.announcementBar.offsetHeight;
      document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
    } else {
      document.documentElement.style.setProperty('--announcement-bar-height', '0px');
    }
  }
  
  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.updateOffset.bind(this));
  }
}

customElements.define('site-header', SiteHeader);


class HeaderPredictiveSearch {
  constructor(form) {
    this.form = form;
    this.input = form.querySelector('.site-header__search-input');
    this.results = form.querySelector('.site-header__predictive');
    this.status = form.querySelector('[data-predictive-status]');
    this.timer = null;
    this.controller = null;

    if (!this.input || !this.results) return;

    this.input.addEventListener('input', () => this.queue());
    this.input.addEventListener('focus', () => {
      if (this.results.childElementCount) this.open();
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    document.addEventListener('click', (event) => {
      if (!this.form.contains(event.target)) this.close();
    });
  }

  queue() {
    window.clearTimeout(this.timer);
    const query = this.input.value.trim();

    if (query.length < 2) {
      this.close(true);
      return;
    }

    this.timer = window.setTimeout(() => this.search(query), 180);
  }

  async search(query) {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    try {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[options][unavailable_products]': 'hide',
        'resources[options][fields]': 'title,product_type,variants.title'
      });
      const response = await fetch('/search/suggest.json?' + params.toString(), {
        signal: this.controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Predictive search request failed');

      const data = await response.json();
      const lmTerms = query.toLowerCase().split(/\s+/).filter(Boolean);
        const lmAll = data.resources?.results?.products || [];
        const lmMatch = lmAll.filter((p) => lmTerms.every((t) => (p.title || '').toLowerCase().includes(t)));
        const products = (lmMatch.length ? lmMatch : lmAll).slice(0, 5);
      await Promise.all(products.map(async (p) => {
        try {
          window.__lmPriceCache = window.__lmPriceCache || {};
          const key = (p.url || '').split('?')[0];
          if (!(key in window.__lmPriceCache)) {
            const r = await fetch(key + '.js', { signal: this.controller.signal });
            const j = await r.json();
            const vs = (j.variants || []).filter((v) => v.price > 0);
            const av = vs.filter((v) => v.available);
            const pool = av.length ? av : vs;
            window.__lmPriceCache[key] = pool.length ? Math.min(...pool.map((v) => v.price)) / 100 : null;
          }
          p.lmPrice = window.__lmPriceCache[key];
        } catch (e) { if (e.name === 'AbortError') throw e; }
      }));
      this.render(products, query);
    } catch (error) {
      if (error.name !== 'AbortError') this.close(true);
    }
  }

  render(products, query) {
    this.results.textContent = '';

    if (!products.length) {
      const empty = document.createElement('span');
      empty.className = 'site-header__predictive-empty';
      empty.textContent = 'Aucun modèle trouvé';
      this.results.appendChild(empty);
    }

    products.forEach((product) => {
      const link = document.createElement('a');
      link.className = 'site-header__predictive-item';
      link.href = product.url;
      link.setAttribute('role', 'option');

      if (product.image) {
        const image = document.createElement('img');
        image.className = 'site-header__predictive-image';
        image.src = product.image;
        image.alt = '';
        image.loading = 'lazy';
        link.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'site-header__predictive-image';
        link.appendChild(placeholder);
      }

      const copy = document.createElement('span');
      copy.className = 'site-header__predictive-copy';
      const title = document.createElement('strong');
      title.className = 'site-header__predictive-title';
      title.textContent = product.title;
      copy.appendChild(title);
      link.appendChild(copy);

      const price = document.createElement('span');
      price.className = 'site-header__predictive-price';
      price.textContent = product.available === false
        ? 'Bientôt disponible'
        : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(product.lmPrice != null ? product.lmPrice : product.price));
      link.appendChild(price);
      this.results.appendChild(link);
    });

    const all = document.createElement('a');
    all.className = 'site-header__predictive-all';
    all.href = '/search?' + new URLSearchParams({
      q: query,
      type: 'product',
      'options[unavailable_products]': 'hide',
      'options[prefix]': 'last'
    }).toString();
    all.textContent = 'Voir tous les résultats';
    this.results.appendChild(all);

    if (this.status) {
      this.status.textContent = products.length + (products.length > 1 ? ' produits trouvés' : ' produit trouvé');
    }
    this.open();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.input.focus();
    }
  }

  open() {
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close(clear = false) {
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    if (clear) this.results.textContent = '';
  }
}

document.querySelectorAll('.site-header__search-form').forEach((form) => {
  new HeaderPredictiveSearch(form);
});

/* Group the four homepage reassurance sections without duplicating their content. */
function groupHomepageReassuranceSections() {
  if (!document.body.classList.contains('template-index') && !document.querySelector('[id$="__why_rent"]')) return;

  const main = document.querySelector('#MainContent');
  const sections = [
    document.querySelector('[id$="__why_rent"]'),
    document.querySelector('[id$="__how_it_works"]'),
    document.querySelector('[id$="__guarantees"]'),
    document.querySelector('[id$="__workshop_fr"]')
  ];

  if (!main || sections.some(section => !section)) return;

  let grid = document.querySelector('.homepage-reassurance-grid');
  if (!grid) {
    grid = document.createElement('div');
    grid.className = 'homepage-reassurance-grid';
    main.insertBefore(grid, sections[0]);
  }

  sections.forEach(section => grid.appendChild(section));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', groupHomepageReassuranceSections);
} else {
  groupHomepageReassuranceSections();
}
document.addEventListener('shopify:section:load', groupHomepageReassuranceSections);

function fitHomepageReassuranceContent() {
  const cards = document.querySelectorAll('.homepage-reassurance-grid > .shopify-section');
  cards.forEach((card) => {
    const content = card.querySelector(':scope > section');
    if (!content) return;

    card.style.position = 'relative';
    content.style.position = 'absolute';
    content.style.maxWidth = 'none';
    content.style.left = '0';
    content.style.top = '0';
    content.style.transform = 'none';
    content.style.transformOrigin = 'center center';

    const candidateWidths = [520, 600, 640, 720, 800, 900, 1000, 1100, 1200];
    let best = null;

    candidateWidths.forEach((width) => {
      content.style.width = width + 'px';
      const height = content.getBoundingClientRect().height;
      const score = Math.abs(Math.log(height / width));
      if (!best || score < best.score) best = { width, height, score };
    });

    content.style.width = best.width + 'px';
    const padding = 0;
    const scaleX = card.clientWidth / best.width;
    const scaleY = card.clientHeight / best.height;

    content.style.left = '50%';
    content.style.top = '50%';
    content.style.transform = 'translate(-50%, -50%) scale(' + scaleX + ', ' + scaleY + ')';
  });
}

let reassuranceResizeTimer;
function scheduleHomepageReassuranceFit() {
  clearTimeout(reassuranceResizeTimer);
  reassuranceResizeTimer = setTimeout(fitHomepageReassuranceContent, 120);
}

scheduleHomepageReassuranceFit();
window.addEventListener('load', scheduleHomepageReassuranceFit);
window.addEventListener('resize', scheduleHomepageReassuranceFit);
document.addEventListener('shopify:section:load', scheduleHomepageReassuranceFit);
document.querySelectorAll('.homepage-reassurance-grid img').forEach((image) => {
  image.addEventListener('load', scheduleHomepageReassuranceFit, { once: true });
});



/* Fermer les sous-menus Samsung au clic extérieur et avec Échap. */
document.addEventListener('click', (event) => { document.querySelectorAll('.lm-samsung-menu[open]').forEach((menu) => { if (!menu.contains(event.target)) menu.open = false; }); });
document.addEventListener('keydown', (event) => { if (event.key !== 'Escape') return; document.querySelectorAll('.lm-samsung-menu[open]').forEach((menu) => { menu.open = false; if (menu.contains(document.activeElement)) menu.querySelector('summary').focus(); }); });

// Mobile search uses the existing form and predictive results.
(function(){
 function initMobileSearch(root){
  root.querySelectorAll('.lm-mobile-search-toggle').forEach(function(button){
   if(button.dataset.bound)return;button.dataset.bound='true';
   var header=button.closest('.site-header');
   function closeSearch(){header.classList.remove('lm-search-open');button.setAttribute('aria-expanded','false');button.setAttribute('aria-label','Ouvrir la recherche')}
   button.addEventListener('click',function(){var open=header.classList.toggle('lm-search-open');button.setAttribute('aria-expanded',String(open));button.setAttribute('aria-label',open?'Fermer la recherche':'Ouvrir la recherche');if(open)header.querySelector('.site-header__search-input').focus()});
   header.addEventListener('keydown',function(event){if(event.key==='Escape'&&header.classList.contains('lm-search-open')){closeSearch();button.focus()}});
  });
 }
 if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',function(){initMobileSearch(document)});else initMobileSearch(document);
 document.addEventListener('shopify:section:load',function(event){initMobileSearch(event.target)});
})();


/* lm-hover-menus : ouverture des menus catalogue au survol (ordinateur uniquement) */
(function(){
 var mq=window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
 var timer;
 function closeAll(except){document.querySelectorAll('.lm-samsung-menu[open]').forEach(function(m){if(m!==except)m.open=false})}
 function bind(root){
  root.querySelectorAll('.lm-samsung-menu').forEach(function(menu){
   if(menu.dataset.lmHover)return;menu.dataset.lmHover='1';
   menu.addEventListener('pointerenter',function(e){if(!mq.matches||e.pointerType==='touch')return;clearTimeout(timer);closeAll(menu);menu.open=true});
   menu.addEventListener('pointerleave',function(e){if(!mq.matches||e.pointerType==='touch')return;clearTimeout(timer);timer=setTimeout(function(){menu.open=false},180)});
   menu.addEventListener('focusin',function(){if(!mq.matches)return;clearTimeout(timer);closeAll(menu);menu.open=true});
   var s=menu.querySelector('summary');
   if(s)s.addEventListener('click',function(e){if(mq.matches&&menu.open){e.preventDefault()}});
  })
 }
 if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',function(){bind(document)});else bind(document);
 document.addEventListener('shopify:section:load',function(e){bind(e.target)});
})();


/* lm-panel-place : garde le panneau dans l'écran */
(function(){
 function place(menu){var p=menu.querySelector('.lm-samsung-panel');if(!p||menu.classList.contains('lm-samsung-menu--mobile'))return;p.style.marginLeft='0px';var r=p.getBoundingClientRect();var pad=12,dx=0;if(r.left<pad)dx=pad-r.left;else if(r.right>window.innerWidth-pad)dx=(window.innerWidth-pad)-r.right;if(dx)p.style.marginLeft=Math.round(dx)+'px'}
 function placeAll(){document.querySelectorAll('.lm-samsung-menu[open]').forEach(place)}
 document.addEventListener('toggle',function(e){var m=e.target;if(m&&m.classList&&m.classList.contains('lm-samsung-menu')&&m.open)place(m)},true);
 document.addEventListener('pointerenter',function(e){var m=e.target&&e.target.closest?e.target.closest('.lm-samsung-menu'):null;if(m&&m.open)place(m)},true);
 window.addEventListener('resize',placeAll);
})();
